import type { ICampaignCategory } from '../../../components/EsForm/CampaignCategorySelector';
import type { IContestFundingAward, IContestFundingRequirements } from '../../../redux/contest/types';
import { prizeDollarsToApiCents } from './buildAwardCategoryPayload';

/** Format integer cents as USD display (e.g. 70000 → "$700.00"). */
export function formatUsdFromCents(cents: number): string {
    return `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** Wallet debit includes platform fees when gross fields are present. */
export function getFundingLockCents(requirements: IContestFundingRequirements): number {
    return requirements.funding_shortfall_gross_cents ?? requirements.funding_shortfall_cents;
}

export function getFundingGrossRequiredCents(requirements: IContestFundingRequirements): number {
    return requirements.gross_required_cents ?? requirements.required_total_cents;
}

export function hasPlatformFeeBreakdown(requirements: IContestFundingRequirements): boolean {
    const fees = requirements.platform_fees;
    if (!fees) return false;
    return (
        getFundingGrossRequiredCents(requirements) > requirements.required_total_cents ||
        fees.flat_fee_cents > 0 ||
        fees.percentage > 0
    );
}

/** Line items for prize pool + platform fees → total wallet lock. */
export function getFundingFeeLineItems(requirements: IContestFundingRequirements): {
    prizePoolCents: number;
    flatFeeCents: number;
    percentageFeeCents: number;
    percentage: number;
    totalFeeCents: number;
    totalFromWalletCents: number;
} {
    const prizePoolCents = requirements.required_total_cents;
    const totalFromWalletCents = getFundingGrossRequiredCents(requirements);
    const example = requirements.platform_fees?.example_for_shortfall;

    if (example) {
        const totalFeeCents = Math.max(0, totalFromWalletCents - prizePoolCents);
        return {
            prizePoolCents,
            flatFeeCents: example.flat_fee_cents,
            percentageFeeCents: example.percentage_fee_cents,
            percentage: example.percentage,
            totalFeeCents,
            totalFromWalletCents,
        };
    }

    const fees = requirements.platform_fees;
    const flatFeeCents = fees?.flat_fee_cents ?? 0;
    const percentage = fees?.percentage ?? 0;
    const totalFeeCents = Math.max(0, totalFromWalletCents - prizePoolCents);
    const percentageFeeCents = Math.max(0, totalFeeCents - flatFeeCents);

    return {
        prizePoolCents,
        flatFeeCents,
        percentageFeeCents,
        percentage,
        totalFeeCents,
        totalFromWalletCents,
    };
}

const MONGO_OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export function resolveFundingTierLabel(raw?: string): string | undefined {
    const trimmed = raw?.trim();
    if (!trimmed || MONGO_OBJECT_ID_RE.test(trimmed)) return undefined;
    return trimmed;
}

export function formatFundingAwardTitle(placement: string, tierLabel?: string): string {
    const tier = resolveFundingTierLabel(tierLabel);
    return tier ? `${placement} · ${tier}` : placement;
}

export interface FundingAwardDisplayRow {
    id: string;
    placement: string;
    tierLabel?: string;
    prizeCents: number;
    displayTitle: string;
}

export function buildFundingAwardRowsFromCategories(categories: ICampaignCategory[]): FundingAwardDisplayRow[] {
    const rows: FundingAwardDisplayRow[] = [];

    for (const category of categories) {
        for (const placement of category.prize_structure || []) {
            for (const payout of placement.payouts || []) {
                const prizeCents = prizeDollarsToApiCents(Number(payout.prize));
                if (prizeCents <= 0) continue;
                const tierLabel = resolveFundingTierLabel(payout.label);
                rows.push({
                    id: `${category.category_name}-${placement.position}-${payout.label}-${prizeCents}`,
                    placement: placement.position,
                    tierLabel,
                    prizeCents,
                    displayTitle: formatFundingAwardTitle(placement.position, tierLabel),
                });
            }
        }
    }

    return rows;
}

function mapApiAwardToRow(
    award: IContestFundingAward,
    wizardRows: FundingAwardDisplayRow[],
    usedWizardIds: Set<string>,
    positionTierLabel?: string,
): FundingAwardDisplayRow {
    const match =
        wizardRows.find(
            w => w.prizeCents === award.prize_cents && w.placement === award.category_title && !usedWizardIds.has(w.id),
        ) ?? wizardRows.find(w => w.prizeCents === award.prize_cents && !usedWizardIds.has(w.id));

    if (match) usedWizardIds.add(match.id);

    const tierLabel =
        match?.tierLabel ??
        resolveFundingTierLabel(award.tier_label ?? award.tier_name) ??
        resolveFundingTierLabel(positionTierLabel);

    return {
        id: award.award_category_id,
        placement: award.category_title,
        tierLabel,
        prizeCents: award.prize_cents,
        displayTitle: formatFundingAwardTitle(award.category_title, tierLabel),
    };
}

/** Awards list for funding UI — includes tier names and every configured prize line. */
export function getFundingAwardDisplayRows(
    requirements: IContestFundingRequirements,
    wizardCategories?: ICampaignCategory[],
): FundingAwardDisplayRow[] {
    const wizardRows = wizardCategories?.length ? buildFundingAwardRowsFromCategories(wizardCategories) : [];
    const usedWizardIds = new Set<string>();

    if (requirements.awards?.length) {
        return requirements.awards.map(award => {
            const positionTier = requirements.positions?.find(p => p.position === award.category_title)?.tier_label;
            return mapApiAwardToRow(award, wizardRows, usedWizardIds, positionTier);
        });
    }

    if (wizardRows.length) return wizardRows;

    return (requirements.positions || []).map(pos => ({
        id: pos.position,
        placement: pos.position,
        tierLabel: resolveFundingTierLabel(pos.tier_label),
        prizeCents: pos.max_prize_cents,
        displayTitle: formatFundingAwardTitle(pos.position, resolveFundingTierLabel(pos.tier_label)),
    }));
}

export function getFundingListedAwardsTotalCents(rows: FundingAwardDisplayRow[]): number {
    return rows.reduce((sum, row) => sum + row.prizeCents, 0);
}

export function parseInsufficientWallet(error: unknown): {
    shortfall_cents: number;
    wallet_balance_cents?: number;
    requested_cents?: number;
    max_fundable_cents?: number;
} | null {
    const err = error as {
        response?: { data?: { data?: Record<string, unknown>; message?: string } };
        data?: { data?: Record<string, unknown> };
    };
    const body = err?.response?.data ?? err?.data;
    const nested = (body?.data ?? body) as Record<string, unknown> | undefined;
    if (nested && typeof nested.shortfall_cents === 'number') {
        return {
            shortfall_cents: nested.shortfall_cents as number,
            wallet_balance_cents: nested.wallet_balance_cents as number | undefined,
            requested_cents: nested.requested_cents as number | undefined,
            max_fundable_cents: nested.max_fundable_cents as number | undefined,
        };
    }
    return null;
}
